import React from "react";
import { Box, Typography, Card, CardContent, Button } from "@mui/material";
import {
  ViewCarousel,
  Build,
  Storefront,
  Timeline,
  Inventory,
  Reviews,
  Settings,
  Category,
  Explore,
  People,
  Edit,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

interface DashboardOverviewProps {
  stats: {
    heroSections: number;
    services: number;
    brands: number;
    processSteps: number;
    featuredEquipment: number;
    testimonials: number;
    siteSettings: number;
    headerCategories: number;
    exploreCategories: number;
    customers: number;
  };
  onTabChange: (tabIndex: number) => void;
}

const DashboardOverview: React.FC<DashboardOverviewProps> = ({
  stats,
  onTabChange,
}) => {
  const { t } = useTranslation();

  const items = [
    {
      title: t("admin.contentManagement.tabs.heroSections"),
      count: stats.heroSections,
      icon: <ViewCarousel />,
      color: "#00d4ff",
      tab: 1,
    },
    {
      title: t("admin.contentManagement.tabs.services"),
      count: stats.services,
      icon: <Build />,
      color: "#ff0080",
      tab: 2,
    },
    {
      title: t("admin.contentManagement.tabs.brands"),
      count: stats.brands,
      icon: <Storefront />,
      color: "#00ff88",
      tab: 3,
    },
    {
      title: t("admin.contentManagement.tabs.headerCategories"),
      count: stats.headerCategories,
      icon: <Category />,
      color: "#007bff",
      tab: 4,
    },
    {
      title: t("admin.contentManagement.tabs.exploreCategories"),
      count: stats.exploreCategories,
      icon: <Explore />,
      color: "#9c27b0",
      tab: 5,
    },
    {
      title: t("admin.contentManagement.tabs.customers"),
      count: stats.customers,
      icon: <People />,
      color: "#e91e63",
      tab: 6,
    },
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        {t("admin.contentManagement.tabs.overview")}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            xl: "repeat(4, 1fr)",
          },
          gap: { xs: 2, sm: 2.5, md: 3 },
        }}
      >
        {items.map((item, index) => (
          <Card
            key={index}
            sx={{
              cursor: "pointer",
              transition: "all 0.3s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: `0 8px 24px ${item.color}20`,
              },
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={() => onTabChange(item.tab)}
          >
            <CardContent
              sx={{ flex: 1, display: "flex", flexDirection: "column" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box
                  sx={{
                    p: { xs: 1, sm: 1.5 },
                    borderRadius: 1,
                    bgcolor: `${item.color}20`,
                    color: item.color,
                    mr: 2,
                  }}
                >
                  {item.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "1.75rem", sm: "2.125rem" },
                    }}
                  >
                    {item.count}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    {item.title}
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                sx={{ color: item.color, mt: "auto" }}
                endIcon={<Edit />}
              >
                {t("admin.contentManagement.manage")}
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default DashboardOverview;